import { useMemo, useRef, useState, useEffect } from 'react';
import { addDays, startOfDay, format, startOfWeek, getDay, getMonth } from 'date-fns';
import { ReservationGeneral } from '../../../../models/owner-portal/reservationGeneral';
import filterCanceledReservations from '../../../../utils/owner-portal/filterCanceledReservation';
import { Card, CardContent, CardHeader } from '../../../../components/ui/Card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../../components/ui/Tooltip';

export interface ChartHeatMapYearProps {
  reservations: ReservationGeneral[];
  startDate: Date;
  endDate: Date;
  filterCanceled?: boolean;
  /** When true, expand cells to fill the card width instead of capping at 14px. */
  fillWidth?: boolean;
}

interface DayCell {
  date: Date;
  revenue: number;
  dayOfWeek: number;
  weekIndex: number;
  monthLabel?: string;
}

/** Returns background color class based on revenue amount. */
function getHeatColor(revenue: number, maxRevenue: number): string {
  if (revenue <= 0 || maxRevenue === 0) return 'bg-muted/30';
  
  const ratio = revenue / maxRevenue;
  if (ratio <= 0.2) return 'bg-primary/25';
  if (ratio <= 0.4) return 'bg-primary/45';
  if (ratio <= 0.6) return 'bg-primary/65';
  if (ratio <= 0.8) return 'bg-primary/85';
  return 'bg-primary';
}

export default function ChartHeatMapYear(props: ChartHeatMapYearProps) {
  const {
    reservations: unfilteredReservations,
    startDate,
    endDate,
    filterCanceled = true,
    fillWidth = false
  } = props;

  const reservations = filterCanceled
    ? filterCanceledReservations(unfilteredReservations)
    : unfilteredReservations;

  const containerRef = useRef<HTMLDivElement>(null);
  const [cellSize, setCellSize] = useState(12);

  // Aggregate revenue by date and build grid structure
  const { grid, maxRevenue, weeks, monthLabels } = useMemo(() => {
    // Step 1: Aggregate revenue by date
    const revenueByDate = new Map<string, number>();
    let max = 0;

    // Helper function to check if a reservation includes a specific day
    const reservationOnThisDay = (reservation: ReservationGeneral, dayToCheck: Date) => {
      // Validate dates
      if (
        !reservation.check_in_ISO ||
        !reservation.check_out_ISO ||
        isNaN(reservation.check_in_ISO.getTime()) ||
        isNaN(reservation.check_out_ISO.getTime())
      ) {
        return false;
      }
      
      const checkIn = startOfDay(reservation.check_in_ISO);
      const checkOut = startOfDay(reservation.check_out_ISO);
      const checkDay = startOfDay(dayToCheck);
      
      // Check if dayToCheck falls between check-in (inclusive) and check-out (exclusive)
      return checkDay >= checkIn && checkDay < checkOut;
    };

    // Iterate through all days in the range
    let currentDay = startOfWeek(startDate, { weekStartsOn: 0 });
    const iterationEnd = endDate;

    while (currentDay <= iterationEnd) {
      const dayKey = format(currentDay, 'yyyy-MM-dd');
      let dayRevenue = 0;
      const day = currentDay;

      // Find all reservations that include this day
      reservations.forEach((r) => {
        if (reservationOnThisDay(r, day)) {
          dayRevenue += r.nightly_rate || 0;
        }
      });

      if (dayRevenue > 0) {
        revenueByDate.set(dayKey, dayRevenue);
        if (dayRevenue > max) max = dayRevenue;
      }

      currentDay = addDays(currentDay, 1);
    }

    // Step 2: Build grid structure (7 rows for days of week, columns for weeks)
    const gridData: DayCell[][] = Array.from({ length: 7 }, () => []);
    const weekIndexMap = new Map<string, number>();
    const monthLabelsMap = new Map<number, string>();
    
    // Start from the first Sunday on or before startDate
    let currentDate = startOfWeek(startDate, { weekStartsOn: 0 });
    let weekIndex = 0;
    let lastMonth = -1;

    while (currentDate <= endDate) {
      const weekKey = format(startOfWeek(currentDate, { weekStartsOn: 0 }), 'yyyy-MM-dd');
      
      if (!weekIndexMap.has(weekKey)) {
        weekIndexMap.set(weekKey, weekIndex);
        weekIndex++;
      }

      const dayOfWeek = getDay(currentDate);
      const dateKey = format(currentDate, 'yyyy-MM-dd');
      const revenue = revenueByDate.get(dateKey) ?? 0;
      const currentMonth = getMonth(currentDate);
      const currentWeekIndex = weekIndexMap.get(weekKey)!;

      // Mark month label on first occurrence
      let monthLabel: string | undefined;
      if (currentMonth !== lastMonth && dayOfWeek === 0) {
        monthLabel = format(currentDate, "MMM ''yy");
        monthLabelsMap.set(currentWeekIndex, monthLabel);
        lastMonth = currentMonth;
      }

      gridData[dayOfWeek].push({
        date: currentDate,
        revenue,
        dayOfWeek,
        weekIndex: currentWeekIndex,
        monthLabel,
      });

      currentDate = addDays(currentDate, 1);
    }

    return {
      grid: gridData,
      maxRevenue: max,
      weeks: weekIndex,
      monthLabels: monthLabelsMap,
    };
  }, [reservations, startDate, endDate]);

  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Calculate cell size to fit container width
  useEffect(() => {
    const updateCellSize = () => {
      if (containerRef.current && weeks > 0) {
        const el = containerRef.current;
        const style = getComputedStyle(el);
        const paddingX =
          (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
        const labelWidth = 40; // Space for day labels (2.5rem)
        const availableWidth = el.clientWidth - paddingX - labelWidth;
        const gap = 2;
        const totalGapWidth = (weeks - 1) * gap;
        const calculatedCellSize = Math.floor((availableWidth - totalGapWidth) / weeks);
        // Keep an 8px floor for readability; only cap at 14px when not filling width
        const clampedSize = fillWidth
          ? Math.max(8, calculatedCellSize)
          : Math.max(8, Math.min(14, calculatedCellSize));
        setCellSize(clampedSize);
      }
    };

    updateCellSize();
    window.addEventListener('resize', updateCellSize);
    return () => window.removeEventListener('resize', updateCellSize);
  }, [weeks, fillWidth]);

  // Keep gap in sync with the sizing math above when filling width
  const cellGap = fillWidth ? 2 : Math.max(1, Math.floor(cellSize / 6));

  return (
    <Card>
      <CardHeader>
        <h3 className="text-sm font-semibold">Booking Heatmap</h3>
        <p className="text-xs text-muted-foreground">
          {reservations.length} reservation{reservations.length === 1 ? '' : 's'} in range.
          {maxRevenue > 0 && ` Max: $${Math.round(maxRevenue)} per day.`}
        </p>
      </CardHeader>
      <CardContent ref={containerRef}>
        <div className="pb-2">
          <div>
            {/* Month labels row */}
            <div className="mb-1 flex" style={{ paddingLeft: '2.5rem' }}>
              {Array.from({ length: weeks }).map((_, weekIdx) => (
                <div
                  key={weekIdx}
                  className="flex-shrink-0 text-[10px] text-muted-foreground"
                  style={{
                    width: `${cellSize}px`,
                    marginRight: weekIdx < weeks - 1 ? `${cellGap}px` : '0',
                  }}
                >
                  {monthLabels.get(weekIdx) || ''}
                </div>
              ))}
            </div>

            {/* Heatmap grid */}
            <div className="flex">
              {/* Day labels column */}
              <div
                className="flex flex-col justify-between pr-2"
                style={{ width: '2.5rem' }}
              >
                {dayLabels.map((label) => (
                  <div
                    key={label}
                    className="flex items-center justify-end text-[10px] text-muted-foreground"
                    style={{ height: `${cellSize}px` }}
                  >
                    {label}
                  </div>
                ))}
              </div>

              {/* Heatmap cells */}
              <TooltipProvider delayDuration={50}>
                <div className="flex flex-col" style={{ gap: `${cellGap}px` }}>
                  {grid.map((row, rowIdx) => (
                    <div key={rowIdx} className="flex" style={{ gap: `${cellGap}px` }}>
                      {row.map((cell, cellIdx) => (
                        <Tooltip key={cellIdx}>
                          <TooltipTrigger asChild>
                            <div
                              className={`rounded-sm transition-colors hover:ring-1 hover:ring-primary cursor-pointer ${getHeatColor(
                                cell.revenue,
                                maxRevenue
                              )}`}
                              style={{
                                height: `${cellSize}px`,
                                width: `${cellSize}px`,
                              }}
                              aria-label={`${format(cell.date, 'E, MMM d, yyyy')}, ${
                                cell.revenue > 0 ? `$${Math.round(cell.revenue)} revenue` : 'no bookings'
                              }`}
                            />
                          </TooltipTrigger>
                          <TooltipContent 
                            side="top" 
                            className="bg-popover text-popover-foreground border border-border shadow-md px-3 py-2"
                          >
                            <div className="text-xs">
                              <div className="font-normal">{format(cell.date, 'E PP')}</div>
                              <div className="font-semibold">
                                {cell.revenue > 0 ? `$${Math.round(cell.revenue)}` : 'No Bookings'}
                              </div>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      ))}
                    </div>
                  ))}
                </div>
              </TooltipProvider>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
